import { unApi } from '../../shared/common/api/endpoints/reports/unApi';
import { ISelectedFilter } from '../../shared/common/components/reports/params/filter/types';

/**
 * Интерфейс элемента списка фильтра
 */
interface FilterListItem {
  id: number | string;
  name: string;
}

/**
 * Обрабатывает фильтры с сохранённым поисковым запросом.
 * Для каждого фильтра с savedSearch выполняет запрос к API,
 * получает список значений и автоматически выбирает все найденные.
 * 
 * @param filters - массив фильтров для обработки
 * @param schoolId - ID школы для запроса
 * @returns Promise с обновлённым массивом фильтров
 */
export async function processSavedSearchFilters(
  filters: ISelectedFilter[],
  schoolId: string
): Promise<ISelectedFilter[]> {
  const processedFilters = await Promise.all(
    filters.map(async (filter) => {
      // Обрабатываем только lazylist фильтры с savedSearch
      if (!filter.savedSearch || filter.type !== 'lazylist') {
        return filter;
      }

      try {
        const response = await unApi.getFilterList(
          parseInt(schoolId),
          filter.id,
          filter.savedSearch
        );

        const items: FilterListItem[] = (response.data as Array<{ id?: number | string; name: string }>)
          .map((item) => ({
            id: item.id?.toString() || '',
            name: item.name?.replace(/&nbsp;/g, ' ') || '',
          }));

        // Возвращаем фильтр с обновлёнными items и checkeds
        return {
          ...filter,
          items,
          checkeds: items, // Автоматически выбираем все найденные значения
        };
      } catch (error) {
        console.error('[processSavedSearchFilters] Error processing saved search:', error);
        // В случае ошибки возвращаем фильтр без изменений
        return filter;
      }
    })
  );

  return processedFilters;
}

/**
 * Проверяет, есть ли в массиве фильтров фильтры с сохранённым поиском
 *
 * @param filters - массив фильтров
 * @returns true если есть хотя бы один фильтр с savedSearch
 */
export function hasSavedSearchFilters(filters: ISelectedFilter[]): boolean {
  return filters.some((filter) => filter.savedSearch && filter.type === 'lazylist');
}

/**
 * Обновляет checkeds в list/lazylist фильтрах, получая актуальные ID с сервера.
 *
 * Решает проблему устаревших ID в сохранённых воронках/сегментах:
 * элемент мог быть пересоздан с новым ID, но с тем же именем.
 *
 * Для list: загружает полный список и сопоставляет по имени (1 запрос на фильтр).
 * Для lazylist: ищет каждый элемент по имени через API (1 запрос на элемент).
 *
 * @param filters - массив фильтров для обработки
 * @param schoolId - ID школы для запроса
 * @returns Promise с обновлённым массивом фильтров
 */
export async function refreshFilterCheckeds(
  filters: ISelectedFilter[],
  schoolId: string
): Promise<ISelectedFilter[]> {
  const hasFiltersToRefresh = filters.some(
    (f) => (f.type === 'lazylist' || f.type === 'list') &&
      Array.isArray(f.checkeds) && f.checkeds.length > 0
  );

  if (!hasFiltersToRefresh) {
    return filters;
  }

  const cleanName = (name: string | undefined): string =>
    (name || '').replace(/&nbsp;/g, ' ').trim();

  const processedFilters = await Promise.all(
    filters.map(async (filter) => {
      if (
        (filter.type !== 'lazylist' && filter.type !== 'list') ||
        !Array.isArray(filter.checkeds) || filter.checkeds.length === 0
      ) {
        return filter;
      }

      try {
        if (filter.type === 'list') {
          // Для list: один запрос — получаем весь список и сопоставляем по имени
          const response = await unApi.getFilterList(
            parseInt(schoolId),
            filter.id
          );
          const allItems: FilterListItem[] = ((response.data || []) as Array<{ id?: number | string; name: string }>)
            .map((item) => ({
              id: item.id?.toString() || '',
              name: cleanName(item.name),
            }));

          const refreshedCheckeds = filter.checkeds.map(
            (checked: FilterListItem) => {
              const checkedClean = cleanName(checked.name as string);
              if (!checkedClean) return checked;
              const match = allItems.find((item) => item.name === checkedClean);
              return match || checked;
            }
          );

          return { ...filter, checkeds: refreshedCheckeds };
        }

        // Для lazylist: ищем каждый элемент по имени через API
        const refreshedCheckeds = await Promise.all(
          filter.checkeds.map(async (checked: FilterListItem) => {
            const checkedClean = cleanName(checked.name as string);
            if (!checkedClean) return checked;

            try {
              const response = await unApi.getFilterList(
                parseInt(schoolId),
                filter.id,
                checkedClean
              );

              const items: FilterListItem[] = ((response.data || []) as Array<{ id?: number | string; name: string }>)
                .map((item) => ({
                  id: item.id?.toString() || '',
                  name: cleanName(item.name),
                }));

              // Точное совпадение по имени
              const match = items.find((item) => item.name === checkedClean);
              return match || checked;
            } catch {
              return checked;
            }
          })
        );

        return { ...filter, checkeds: refreshedCheckeds };
      } catch (error) {
        console.error('[refreshFilterCheckeds] Error refreshing filter:', filter.id, error);
        return filter;
      }
    })
  );

  return processedFilters;
}
